import { Close, FileDownload } from "@mui/icons-material";
import { Box, IconButton, Modal, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { type DownloadFormat, DownloadFormatButton } from "../../components/UIToolkit/Clickable/DownloadFormatButton";
import { SystemFlatSwitch } from "../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SpreadsheetPreview } from "../../components/UIToolkit/Previews/SpreadsheetPreview";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { generatePrintoutPdf } from "../../downloads/downloadDayViewPdf";
import { handleDownload } from "../../downloads/utils";
import { excelBufferToFortuneSheet } from "../../utils/excelToFortuneSheet";
import type { DisciplinaryReportRowData, DisciplinaryViolationType, NurseSortItem } from "../../utils/models";
import type { SortingState } from "../../utils/multiColumnSorting";
import { useExportDisciplinaryReport } from "./useDisciplinaryReport";

type DisciplinaryExportButtonProps = {
	dateSortOrder: "asc" | "desc";
	nurseSort?: SortingState<DisciplinaryReportRowData>[];
	startDate: DateTime;
	endDate: DateTime;
	hasData: boolean;
	roles?: string[];
	employmentTypes?: string[];
	shiftTypes?: string[];
	shiftHours?: string[];
	violationTypes?: DisciplinaryViolationType[];
};

export const DisciplinaryExportButton: FC<DisciplinaryExportButtonProps> = ({
	dateSortOrder,
	nurseSort,
	startDate,
	endDate,
	hasData,
	roles,
	employmentTypes,
	shiftTypes,
	shiftHours,
	violationTypes,
}) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	const { mutateAsync: getBufferAsync } = useExportDisciplinaryReport();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [exportFormat, setExportFormat] = useState<"pretty" | "raw" | "overview">("pretty");
	const [downloadLoading, setDownloadLoading] = useState(false);

	const openModal = () => {
		track("disciplinary_export_opened");
		setIsModalOpen(true);
	};

	const closeModal = () => {
		setIsModalOpen(false);
	};

	const {
		data: previewData,
		isFetching: isLoadingPreview,
		error: previewError,
		refetch: refetchPreview,
	} = useQuery({
		queryKey: [
			"disciplinary-export-preview",
			exportFormat,
			dateSortOrder,
			startDate.toISODate(),
			endDate.toISODate(),
			nurseSort,
			roles,
			employmentTypes,
			shiftTypes,
			shiftHours,
			violationTypes,
		],
		enabled: isModalOpen,
		retry: false,
		queryFn: async () => {
			try {
				const exportParams = {
					exportFormat,
					dateSortOrder,
					nurseSort: nurseSort?.map<NurseSortItem>((sortItem) => ({
						column: sortItem.sortColumns[0],
						order: sortItem.sortOrder,
					})),
					startDate,
					endDate,
					roles,
					employmentTypes,
					shiftTypes,
					shiftHours,
					violationTypes,
				};
				const buffer = await getBufferAsync(exportParams);
				const sheets = await excelBufferToFortuneSheet(buffer);
				return { buffer, sheets };
			} catch (error) {
				trackError("disciplinary_export_preview", {
					error_message: (error as Error)?.message,
				});
				throw error;
			}
		},
	});

	const previewSheets = previewData?.sheets ?? [];
	const buffer = previewData?.buffer ?? null;

	const onDownload = (format: DownloadFormat) => {
		if (!buffer) return;
		setDownloadLoading(true);
		try {
			track("disciplinary_export_download_start", { format });
			const baseName = `disciplinary_report_${startDate.toFormat("yyyy-MM-dd")}_${endDate.toFormat("yyyy-MM-dd")}`;
			// The PDF renders from the sheets already on screen, so it needs no second request.
			if (format === "pdf") {
				// No printout panel here either, so the workbook decides. The backend writes no page setup at
				// all, which means Excel's own defaults — portrait, and the renderer's one-page-wide fallback.
				handleDownload(generatePrintoutPdf(previewSheets, null, null, null, null), `${baseName}.pdf`, "application/pdf");
			} else {
				handleDownload(buffer, `${baseName}.xlsx`);
			}
			track("disciplinary_export_download_success", { format });
			closeModal();
		} catch (error) {
			// Closing here would look exactly like success. Stay open, say what happened, let them retry.
			trackError("disciplinary_export_download_error", { format, error_message: (error as Error)?.message });
			showErrorSnackbar(t(format === "pdf" ? "download.pdf.failed" : "download.excel.failed"), {}, error);
		} finally {
			setDownloadLoading(false);
		}
	};

	return (
		<>
			<Tooltip title="Export to Excel">
				<span>
					<IconButton onClick={openModal} size="small" sx={{ color: "primary.dark" }} disabled={!hasData}>
						<FileDownload />
					</IconButton>
				</span>
			</Tooltip>
			<Modal open={isModalOpen} onClose={closeModal} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Box
					sx={{
						bgcolor: "background.paper",
						borderRadius: 2,
						p: 3,
						width: "95vw",
						maxWidth: 1400,
						height: "85vh",
						outline: "none",
						boxShadow: 24,
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
						<Typography variant="h6" sx={{ fontWeight: 600, color: "primary.dark" }}>
							Disciplinary Report Export
						</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
							<SystemFlatSwitch
								options={[
									{ label: "Pretty", value: "pretty" as const },
									{ label: "Raw", value: "raw" as const },
									{ label: "Overview", value: "overview" as const },
								]}
								activeIndexOrValue={exportFormat}
								onSwitch={(option) => setExportFormat(option.value)}
							/>
							<IconButton onClick={closeModal} size="small">
								<Close />
							</IconButton>
						</Box>
					</Box>

					<SpreadsheetPreview
						dataTestId="disciplinary-export-preview"
						sheets={previewSheets}
						isLoading={isLoadingPreview}
						// A rejected request also yields zero sheets, and with `retry: false` there was no way
						// back from one failure.
						emptyLabel={previewError ? t("download.preview.error") : undefined}
						action={
							previewError ? (
								<SystemButton data-testid="disciplinary-export-retry" buttonVariant="secondary" size="small" onClick={() => void refetchPreview()}>
									{t("retry")}
								</SystemButton>
							) : undefined
						}
						sx={{ flex: 1 }}
					/>

					<Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
						<DownloadFormatButton
							dataTestIdPrefix="disciplinary-export"
							formats={["xlsx", "pdf"]}
							onDownload={onDownload}
							isDownloading={downloadLoading}
							disabled={isLoadingPreview || previewSheets.length === 0}
						/>
					</Box>
				</Box>
			</Modal>
		</>
	);
};
